import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import once from 'lodash/once';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { showErrorFlag } from 'state/actions/flags';
import type { ShowErrorFlag } from 'state/actions/flags';
import { mapDispatchToPropsBanner } from 'state/persisted/blocked-apps-banner/reducer';
import type { BlockedAppsBannerActions } from 'state/persisted/blocked-apps-banner/reducer';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { ForgeCustomFieldEdit } from 'view/forge-ui/custom-field';
import { getExtensionIdFromLegacyFieldType, isForgeLegacyFieldType } from 'view/forge-ui/custom-field/utils';
import { createAttributes, fireOperationalEvent, fireScreenEvent } from 'view/forge-ui-analytics/common';
import {
    PACKAGE_NAME_GASV3,
    SOURCES,
    EXPERIENCES,
    RENDER_CONTEXTS,
    OPERATIONAL_ACTIONS,
} from 'view/forge-ui-analytics/common/constants';
import {
    createInitializationFinished,
    createInitializationFailed,
} from 'view/forge-ui-analytics/services/fetch-modules';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import { useInlineField } from '@atlassian/help-center-common-component/forge-custom-fields/controllers/store';
import {
    CUSTOM_FIELD,
    CUSTOM_FIELD_TYPE,
    FORGE_REQUEST_CREATE,
    FORGE_REQUEST_DETAILS,
} from '@atlassian/help-center-common-component/forge-ui/constants';
import { ExtensionTitle } from '@atlassian/help-center-common-component/forge-ui/extension-title';
import { AtlassianHelpConfigAccountType } from '@atlassian/help-center-common-component/forge-ui/types';
import type {
    ExtensionPointModule,
    CustomField,
    CustomFieldType,
    CustomFieldExtensionData,
    CustomFieldExtensionPayload,
    WithBlockedAppsBannerProps,
} from '@atlassian/help-center-common-component/forge-ui/types';
import { useShowBanner } from '@atlassian/help-center-common-util/blocked-apps-banner';
import { getMetaFromHTML } from '@atlassian/help-center-common-util/meta';
import { FIELD_STATE_FAILED } from '../../forge-custom-fields/controllers/store/constants';
import { useExtensions } from '../../forge-ui/use-extensions';
import { ErrorMessage } from './error-message';
import { FieldState } from './field-state';
import messages from './messages';
import { Skeleton } from './skeleton';
import { getMappedFieldType } from './utils/utils';
import type { FieldType } from 'state/persisted/request-create/types';
import type { AnalyticsAttributes, Source } from 'view/forge-ui-analytics/common/types';

interface Props extends FieldFormProps<string> {
    entryPoint: 'view' | 'edit';
    value?: string;
    jiraFieldType: string;
    disabled: boolean;
    portalId: number;
    requestTypeId: number;
    onChange: (name: string, value: unknown, notFromUserInteraction?: boolean) => void;
    enableSubmitButton: () => void;
    disableSubmitButton: () => void;
    fallbackRender: (newType: FieldType) => JSX.Element;
}

interface WithConnectedProps extends Props, WithBlockedAppsBannerProps {
    showErrorFlag: typeof showErrorFlag;
}

interface PropsWithAnalyticsAttributes extends WithConnectedProps {
    analyticsAttributes: AnalyticsAttributes;
}

const ForgeFieldWithExtension: React.FC<
    PropsWithAnalyticsAttributes & {
        extension: CustomField | CustomFieldType;
        loadingComponent: React.ReactNode;
        isVisible: boolean;
    }
> = ({
    id,
    value,
    extension,
    requestTypeId,
    portalId,
    disabled,
    entryPoint,
    loadingComponent,
    isVisible,
    onChange,
}) => {
    di(ForgeCustomFieldEdit, FieldState);

    const extensionData: CustomFieldExtensionData = useMemo(
        () => ({
            fieldType: extension.id,
            fieldId: id,
            type: extension.type,
            portal: {
                id: portalId,
            },
            request: {
                typeId: requestTypeId,
            },
            ...(entryPoint === 'view'
                ? {
                      renderContext: RENDER_CONTEXTS.PORTAL_VIEW,
                      experience: EXPERIENCES.PORTAL_VIEW,
                      entryPoint: 'view',
                  }
                : {
                      renderContext: RENDER_CONTEXTS.PORTAL_REQUEST,
                      experience: EXPERIENCES.PORTAL_REQUEST,
                      entryPoint: 'edit',
                  }),
        }),
        [extension.id, extension.type, id, portalId, requestTypeId, entryPoint]
    );

    const extensionPayload: CustomFieldExtensionPayload = useMemo(() => ({ fieldValue: value }), [value]);
    const onSubmit = useCallback(
        (fieldValue: unknown) => onChange(extensionData.fieldId, fieldValue, true),
        [extensionData.fieldId, onChange]
    );

    if (entryPoint === 'edit') {
        return (
            <FieldState isDisabled={disabled} isVisible={isVisible}>
                <ForgeCustomFieldEdit
                    extension={extension}
                    extensionData={extensionData}
                    extensionPayload={extensionPayload}
                    loadingComponent={loadingComponent}
                    onSubmit={onSubmit}
                />
            </FieldState>
        );
    }

    return null;
};

const onInitializationFinished = createInitializationFinished(SOURCES.PORTAL_REQUEST, true);
const onInitializationFailed = createInitializationFailed(SOURCES.PORTAL_REQUEST, true);

const ForgeFieldImpl = (props: PropsWithAnalyticsAttributes) => {
    di(
        getMetaFromHTML,
        useExtensions,
        FieldBase,
        Skeleton,
        useInlineField,
        fireScreenEvent,
        fireOperationalEvent,
        ErrorMessage,
        useIntl
    );

    const {
        disableSubmitButton,
        enableSubmitButton,
        fallbackRender,
        jiraFieldType,
        entryPoint,
        id,
        analyticsAttributes,
    } = props;
    const [isRendered, setIsRendered] = useState(false);
    const accountType = getMetaFromHTML('ajs-account-type') || AtlassianHelpConfigAccountType.ANONYMOUS;
    const hasAccess = accountType === AtlassianHelpConfigAccountType.ATLASSIAN;
    const [fieldState] = useInlineField(id);
    const { formatMessage } = useIntl();

    const fireScreenEventOnce = useMemo(() => once(fireScreenEvent), []);
    const fireNotFoundOnce = useMemo(() => once(fireOperationalEvent), []);
    const showNotFoundFlagOnce = useMemo(() => once(props.showErrorFlag), [props.showErrorFlag]);

    const useExtensionsConfig = useMemo(
        () => ({
            onInitializationFinished,
            onInitializationFailed,
            types: [CUSTOM_FIELD, CUSTOM_FIELD_TYPE] as ExtensionPointModule[],
            context: {
                portalId: String(props.portalId),
                requestTypeId: String(props.requestTypeId),
            },
            includeHidden: true,
            shouldFetch: hasAccess,
        }),
        [hasAccess, props.portalId, props.requestTypeId]
    );

    const { extensions, isLoading, error } = useExtensions<CustomField | CustomFieldType>(useExtensionsConfig);
    const onRendered = useCallback(() => {
        setIsRendered(true);
        // eslint-disable-next-line no-void
        void fireScreenEventOnce({
            attributes: { ...analyticsAttributes, entryPoint },
            id: entryPoint === 'view' ? `customFieldScreen` : 'customFieldEditScreen',
            source: entryPoint === 'view' ? SOURCES.PORTAL_VIEW : SOURCES.PORTAL_REQUEST,
        });
    }, [analyticsAttributes, entryPoint, fireScreenEventOnce]);

    useEffect(() => {
        isLoading ? disableSubmitButton() : enableSubmitButton();
    }, [isLoading, disableSubmitButton, enableSubmitButton]);

    const extension = useMemo(() => {
        const extensionId = isForgeLegacyFieldType(jiraFieldType)
            ? getExtensionIdFromLegacyFieldType(jiraFieldType)
            : jiraFieldType;
        return extensions.find((field) => field.id === extensionId);
    }, [extensions, jiraFieldType]);

    // We are going to pass to the list only the extension that is found.
    // This is to avoid showing the banner for the extensions that are not added to the experience but still installed on the tenant.
    const extensionsToCheck = useMemo(() => (extension ? [extension] : []), [extension]);
    useShowBanner(
        extensionsToCheck,
        entryPoint === 'view' ? FORGE_REQUEST_DETAILS : FORGE_REQUEST_CREATE,
        hasAccess,
        props.showBanner,
        props.resetBannerState
    );

    const shouldFallBackToNativeRendering = useMemo(() => {
        // Too early to tell, just assume we have a custom renderer
        if (!extension) return false;
        // There is no resource, so it's not a custom renderer
        if (typeof extension.properties[entryPoint]?.resource !== 'string') return true;
        // There must be a correct experience defined, otherwise we'd need to fallback to the native rendering
        const isValidExperience =
            entryPoint === 'view'
                ? extension.properties[entryPoint]?.experience?.includes(EXPERIENCES.PORTAL_VIEW)
                : extension.properties[entryPoint]?.experience?.includes(EXPERIENCES.PORTAL_REQUEST);
        return !isValidExperience;
    }, [extension, entryPoint]);

    const preferredFieldNode = useMemo(() => {
        if (shouldFallBackToNativeRendering && extension) {
            return fallbackRender(getMappedFieldType(extension));
        }
        return null;
    }, [extension, fallbackRender, shouldFallBackToNativeRendering]);

    const fieldBaseProps = useMemo(
        () => ({
            label: extension ? (
                <ExtensionTitle
                    extensionName={props.label}
                    extensionEnvType={extension.environmentType}
                    extensionEnvKey={extension.environmentKey}
                />
            ) : (
                props.label
            ),
            htmlFor: props.id,
            required: props.required,
            description: props.description,
            labelId: `${props.id}-label`,
        }),
        [props.description, props.id, props.label, props.required, extension]
    );

    const mergedErrorMessages = useMemo(
        () =>
            [
                fieldState?.status === FIELD_STATE_FAILED && formatMessage(messages.errorMessage),
                props.errorMessage,
            ].filter(Boolean) as string[],
        [fieldState?.status, props.errorMessage, formatMessage]
    );

    const isHidden = typeof extension?.hiddenBy === 'string';

    if (!hasAccess || isHidden || error) {
        return null;
    }

    if (!extension && !isLoading) {
        // eslint-disable-next-line no-void
        void fireNotFoundOnce(OPERATIONAL_ACTIONS.FAILED, {
            attributes: {
                ...analyticsAttributes,
                entryPoint,
                reason: 'notFound',
                required: props.required,
            },
            id: entryPoint === 'view' ? `customField` : 'customFieldEdit',
            source: entryPoint === 'view' ? SOURCES.PORTAL_VIEW : SOURCES.PORTAL_REQUEST,
        });
        if (props.required) {
            showNotFoundFlagOnce(
                messages.notFoundRequiredFieldTitle,
                messages.notFoundRequiredFieldDescription,
                [],
                undefined,
                { fieldName: props.label }
            );
        }
        return null;
    }

    if (preferredFieldNode) {
        return preferredFieldNode;
    }

    return (
        <FieldBase {...fieldBaseProps} errorMessage={<ErrorMessage errorMessages={mergedErrorMessages} />}>
            {isLoading || !isRendered ? <Skeleton /> : null}
            {extension && (
                <ForgeFieldWithExtension
                    {...props}
                    extension={extension}
                    loadingComponent={<Skeleton onUnmount={onRendered} />}
                    isVisible={isRendered}
                />
            )}
        </FieldBase>
    );
};

export const ForgeFieldWithErrorBoundary = (props: WithConnectedProps) => {
    di(ForgeFieldImpl, ErrorBoundary, createAttributes);

    const id = props.entryPoint === 'view' ? `customField` : 'customFieldEdit';
    const rawAttributes: {
        source: Source;
        renderContext: string;
        experience: string;
    } = useMemo(
        () =>
            props.entryPoint === 'view'
                ? {
                      source: SOURCES.PORTAL_VIEW,
                      renderContext: RENDER_CONTEXTS.PORTAL_VIEW,
                      experience: EXPERIENCES.PORTAL_VIEW,
                  }
                : {
                      source: SOURCES.PORTAL_REQUEST,
                      renderContext: RENDER_CONTEXTS.PORTAL_REQUEST,
                      experience: EXPERIENCES.PORTAL_REQUEST,
                  },
        [props.entryPoint]
    );
    const analyticsAttributes = useMemo(() => createAttributes(rawAttributes), [rawAttributes]);

    return (
        <ErrorBoundary id={id} packageName={PACKAGE_NAME_GASV3} analyticsAttributes={analyticsAttributes}>
            <ForgeFieldImpl {...props} analyticsAttributes={rawAttributes} />
        </ErrorBoundary>
    );
};

const mapDispatchToProps = (dispatch: React.Dispatch<BlockedAppsBannerActions | ShowErrorFlag>) => {
    return {
        ...mapDispatchToPropsBanner(dispatch),
        showErrorFlag: (...args: Parameters<typeof showErrorFlag>): void => dispatch(showErrorFlag(...args)),
    };
};

export const ForgeField = connect<null, ReturnType<typeof mapDispatchToProps>>(
    null,
    mapDispatchToProps
)(ForgeFieldWithErrorBoundary);
